import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { formatNumber } from "../../../../components/design-system/admin";
import type { OverviewVendorRow } from "../../../../common/models/admin-overview.model";
import { Vendor } from "../models/vendor.model";
import { useVendorsApi } from "../../../../common/hooks/use-vendors-api.hook";
import { VendorLogo } from "./vendor-row-parts.component";

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex items-baseline justify-between gap-4 border-b py-2 last:border-b-0">
        <dt className="text-sm text-muted-foreground">{label}</dt>
        <dd className="min-w-0 text-end text-sm">{value}</dd>
    </div>
);

/**
 * A quick look at one vendor, opened from its logo in the list. Opens once the
 * vendor has loaded (never on an empty dialog); a failed load just closes it.
 */
export const VendorView = ({
    selectedVendorForViewID,
    setSelectedVendorForView,
    usage,
    onEdit,
}: {
    selectedVendorForViewID: string;
    setSelectedVendorForView: (id: string | null) => void;
    /** The vendor's overview row: plan and V2 product count. */
    usage?: OverviewVendorRow;
    onEdit?: (vendor: Vendor) => void;
}) => {
    const { t } = useTranslation();
    const [vendor, setVendor] = useState<Vendor>();
    const vendorsAPI = useVendorsApi();

    useEffect(() => {
        let cancelled = false;
        const getVendor = async () => {
            try {
                const result = await vendorsAPI.findOneVendor(selectedVendorForViewID);
                if (!cancelled) setVendor(result || undefined);
                if (!cancelled && !result) setSelectedVendorForView(null);
            } catch (error) {
                console.error('Error fetching vendor:', error);
                if (!cancelled) setSelectedVendorForView(null);
            }
        };
        getVendor();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVendorForViewID]);

    const areas = (vendor?.areas || []).map((a) => a?.name?.replace(/_/g, ' ')).filter(Boolean);

    return (
        <Dialog
            open={!!vendor}
            onOpenChange={(open) => {
                if (!open) setSelectedVendorForView(null);
            }}
        >
            <DialogContent className="sm:max-w-lg" data-testid="vendor-view">
                {vendor && (
                    <>
                        <DialogHeader>
                            <div className="flex items-center gap-3">
                                <VendorLogo vendor={vendor} className="size-12" />
                                <div className="min-w-0">
                                    <DialogTitle className="truncate font-heading text-lg font-medium tracking-tight">
                                        {vendor.name}
                                    </DialogTitle>
                                    <DialogDescription className="truncate">@{vendor.alias}</DialogDescription>
                                </div>
                            </div>
                        </DialogHeader>

                        <dl>
                            <DetailRow label={t('ADMIN_VENDORS.VIEW.EMAIL')} value={vendor.email || '—'} />
                            <DetailRow label={t('ADMIN_VENDORS.VIEW.PLAN')} value={usage?.planName || '—'} />
                            <DetailRow
                                label={t('ADMIN_VENDORS.VIEW.PRODUCTS')}
                                value={<span className="tabular-nums">{usage ? formatNumber(usage.products) : '—'}</span>}
                            />
                            <DetailRow
                                label={t('ADMIN_VENDORS.VIEW.SPACES')}
                                value={<span className="tabular-nums">{formatNumber(vendor.spaces?.length ?? 0)}</span>}
                            />
                            <DetailRow
                                label={t('ADMIN_VENDORS.VIEW.AREAS')}
                                value={<span className="capitalize">{areas.length ? areas.join(', ') : '—'}</span>}
                            />
                        </dl>

                        {onEdit && (
                            <DialogFooter>
                                <Button variant="outline" onClick={() => onEdit(vendor)}>
                                    <Pencil className="size-4" />
                                    {t('ADMIN_VENDORS.MENU.EDIT')}
                                </Button>
                            </DialogFooter>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
